import React, { useCallback, useLayoutEffect, useState } from 'react';

/**
 * Hook for setting up an IntersectionObserver on a given element.
 * @param callback A callback to be invoked when an intersection event is observed
 *
 * @returns A callback ref to be set on the element to be observed.
 */
const useIntersectionObserver = <TElement extends Element>(
  callback: (entry: IntersectionObserverEntry) => void,
) => {
  const [element, setElement] = useState<TElement | null>(null);

  useLayoutEffect(() => {
    if (element) {
      const intersectionObserver = new IntersectionObserver(
        ([entry]) => {
          callback(entry);
        },
        { threshold: [0, 1] },
      );

      intersectionObserver.observe(element);

      return () => {
        intersectionObserver.disconnect();
      };
    }
    return undefined;
  }, [element, callback]);

  return useCallback((node: TElement | null) => {
    setElement(node);
  }, []);
};

type StickEventHandler = (isStuck: boolean) => void;

interface StickyIndicatorProps {
  onStuckChange: StickEventHandler;
}

const StickyFooterIndicator = ({ onStuckChange }: StickyIndicatorProps) => {
  const ref = useIntersectionObserver(
    useCallback(
      (e) => {
        // The element being monitored is considered "stuck" as long as the indicator is below the
        // viewport (i.e., neither intersecting the viewport, nor above it).
        const isAboveViewport = e.boundingClientRect.top <= 0;
        const isStuck = !e.isIntersecting && !isAboveViewport;

        onStuckChange(isStuck);
      },
      [onStuckChange],
    ),
  );

  return (
    <div
      ref={ref}
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: -1,
        visibility: 'hidden',
      }}
    />
  );
};

const stickyDisabledResult = { isStuck: false, stickyIndicator: null };

interface UseStickyFooterIndicatorReturnType {
  /**
   * Whether the sticky element is currently "stuck". This may be `undefined` until the
   * `stickyIndicator` has rendered and its corresponding intersection observer callback has had a
   * chance to run.
   *
   * Note: if the element's parent is entirely below the viewport, the element will be considered
   * "stuck" even though it is not actually visible.
   */
  isStuck: boolean | undefined;

  /**
   * A special "indicator" element that must be rendered to determine the sticky element's state.
   * This element must be rendered as the last child of the sticky element being monitored.
   */

  stickyIndicator: JSX.Element | null;
}

interface UseStickyFooterIndicatorParams {
  /**
   * Whether to enable sticky behavior or not.
   */
  enableSticky: boolean;
}

/**
 * A hook to help identify whether a sticky footer element is currently "stuck" or not.
 */
export const useStickyFooterIndicator = ({
  enableSticky,
}: UseStickyFooterIndicatorParams): UseStickyFooterIndicatorReturnType => {
  const [isStuck, setIsStuck] = useState<boolean>();

  if (!enableSticky) {
    return stickyDisabledResult;
  }

  const stickyIndicator = <StickyFooterIndicator onStuckChange={setIsStuck} />;

  return { isStuck, stickyIndicator };
};
